// Units: the app works in gallons/inches/pounds inside; metric drivers see liters/cm/kg on screen.
const PREF = "fhp-units";
const US_REGIONS = new Set(["US", "LR", "MM", "PR", "GU", "VI", "AS", "MP"]);
const US_LANGS = new Set(["en", "es"]);
const L = 3.78541, CM = 2.54, KG = 0.453592;

export const unitPref = () => { try { return localStorage.getItem(PREF) || "auto"; } catch { return "auto"; } };
export const setUnitPref = (p) => { try { localStorage.setItem(PREF, p); } catch { /* private mode */ } window.location.reload(); };

// Automatic: the phone's country decides; with no country, the app language does.
export function autoMetric() {
  for (const l of navigator.languages || [navigator.language || ""]) {
    const region = (l || "").split("-")[1];
    if (region && /^[A-Za-z]{2}$/.test(region)) return !US_REGIONS.has(region.toUpperCase());
  }
  let lang = "en";
  try { lang = localStorage.getItem("fhp-lang") || (navigator.language || "en").split("-")[0]; } catch { /* private mode */ }
  return !US_LANGS.has(lang);
}

export const METRIC = (() => { const p = unitPref(); return p === "metric" || (p === "auto" && autoMetric()); })();

const num = (s) => Number(String(s).replace(/,/g, ""));
const fmt = (v, dec = 0) => v.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });
const FR = { "⅛": 0.125, "¼": 0.25, "⅜": 0.375, "½": 0.5, "⅝": 0.625, "¾": 0.75, "⅞": 0.875 };
const N = "(\\d[\\d,]*(?:\\.\\d+)?|\\.\\d+)";
const GAL_W = "gal|gallons?|galones|galón|galão|galões|gallone[ni]?|gallonen?|galonów|galoane|галлон\\p{L}*|гал\\.?|галон\\p{L}*|γαλόνι\\p{L}*|galon\\p{L}*|غالون|جالون|گالن|گیلن|גלון|गैलन|ਗੈਲਨ|গ্যালন|ગેલન|கேலன்|加仑|ガロン|갤런|แกลลอน|galoni|ጋሎን|galèn";
const IN_W = "in|inches|inch|pulgadas?|pouces?|zoll|polegadas?|pollici|cali|inç|дюйм\\p{L}*|ίντσ\\p{L}*|بوصة|إنش|اینچ|انچ|אינץ'?|इंच|ਇੰਚ|ইঞ্চি|ઇંચ|அங்குலம்|英寸|インチ|인치|นิ้ว|inci|inchi|ኢንች|pous";
const LB_W = "lbs?|libras?|livres?|pfund|funt\\p{L}*|libbre|фунт\\p{L}*|λίβρ\\p{L}*|pound|رطل|پوند|پاؤنڈ|פאונד|ליברות|पाउंड|ਪੌਂਡ|পাউন্ড|પાઉન્ડ|பவுண்டு|磅|ポンド|파운드|ปอนด์|pauni|ፓውንድ|liv";
const END = "(?![\\p{L}\\p{M}])";
const RULES = [
  [/(\d+(?:\.\d+)?)-(inch|foot)\b/g, (_, a, u) => (u === "inch" ? `${fmt(num(a) * CM, 0)} cm` : `${fmt(num(a) * 0.3048, 1)} m`)],
  [new RegExp(`${N}\\s?[–-]\\s?${N}\\s?(lbs?|gal|in|ft)\\b`, "g"), (_, a, b, u) => { const [f, d, l] = u === "gal" ? [L, 0, "L"] : u === "in" ? [CM, 1, "cm"] : u === "ft" ? [0.3048, 1, "m"] : [KG, 0, "kg"]; return `${fmt(num(a) * f, d)}–${fmt(num(b) * f, d)} ${l}`; }],
  [/(\d+) ft (\d+(?:\.\d+)?) in\b/g, (_, f, i) => `${fmt((num(f) * 12 + num(i)) * CM / 100, 1)} m`],
  [new RegExp(`${N}\\s?(?:ft|feet)\\b`, "g"), (_, a) => `${fmt(num(a) * 0.3048, 1)} m`],
  [new RegExp(`${N}\\s?(?:gal\\/in|gpi)\\b`, "g"), (_, a) => `${fmt(num(a) * L / CM, 1)} L/cm`],
  [new RegExp(`${N}(\\s?)(?:${GAL_W})${END}`, "giu"), (_, a, sp) => `${fmt(num(a) * L)}${sp || " "}L`],
  [/(\d+)-([⅛¼⅜½⅝¾⅞]) in\b/g, (_, w, f) => `${fmt((num(w) + FR[f]) * CM, 1)} cm`],
  [/(^|[^\d])([⅛¼⅜½⅝¾⅞]) in\b/g, (_, p, f) => `${p}${fmt(FR[f] * CM, 1)} cm`],
  [new RegExp(`${N}\\s?(?:${IN_W})${END}`, "giu"), (_, a) => `${fmt(num(a) * CM, 1)} cm`],
  [new RegExp(`${N}(?:″|")`, "g"), (_, a) => `${fmt(num(a) * CM, 1)} cm`],
  [new RegExp(`${N}\\s?(?:${LB_W})${END}`, "giu"), (_, a) => `${fmt(num(a) * KG, num(a) < 100 ? 1 : 0)} kg`],
  [/\((?:inches|in)\)/g, () => "(cm)"],
];

export function metricize(s) {
  if (!METRIC || typeof s !== "string" || !/[\d½¼¾⅛⅜⅝⅞]|\(/.test(s)) return s;
  return RULES.reduce((acc, [re, fn]) => acc.replace(re, fn), s);
}

// Stepper/number inputs: convert to what the driver sees, and back.
export const UNIT = {
  in: { f: CM, label: "cm", step: 0.5, dec: 1 },
  gal: { f: L, label: "L", step: 250, dec: 0 },
  lb: { f: KG, label: "kg", step: 100, dec: 0 },
  ft: { f: 0.3048, label: "m", step: 0.5, dec: 1 },
};
export const toShown = (v, unit) => (METRIC && UNIT[unit] ? Math.round(v * UNIT[unit].f * 10 ** UNIT[unit].dec) / 10 ** UNIT[unit].dec : v);
export const fromShown = (v, unit) => (METRIC && UNIT[unit] ? v / UNIT[unit].f : v);

// Rewrites unit text React renders anywhere on the page (cards, tables, charts) when metric is on.
const SPLIT = /^(\s?)(gal|in|lb|lbs|″|")(?=$|[\s·,).:/])/;
const NUMERIC = /^[+−-]?\d[\d,]*(?:\.\d+)?$/;
const SPLIT_CONV = { gal: [L, 0, "L"], in: [CM, 1, "cm"], lb: [KG, 0, "kg"], lbs: [KG, 0, "kg"], "″": [CM, 1, "cm"], '"': [CM, 1, "cm"] };
const convNum = (txt, u) => { const [f, dec, ] = SPLIT_CONV[u]; const sign = /^[+−-]/.test(txt) ? txt[0] : ""; return sign + fmt(num(txt.replace(/^[+−-]/, "")) * f, dec); };

export function startMetricText(root) {
  if (!METRIC) return;
  const fix = (node) => {
    if (node.nodeType !== 3 || !node.data || node.data === node.__conv) return;
    const p = node.parentElement;
    if (!p || p.closest("script,style,textarea,[data-no-metric]")) return;
    // React splits "{n} gal" into two text nodes: convert the number node and relabel the unit node.
    const ps = node.previousSibling ?? (p.firstChild === node ? p.previousSibling : null);
    const prev = ps?.nodeType === 3 ? ps : (ps?.nodeType === 1 && ps.childNodes.length === 1 && ps.firstChild.nodeType === 3 ? ps.firstChild : null);
    const m = node.__unit ? null : node.data.match(SPLIT);
    if (m && prev && NUMERIC.test(prev.data.trim())) {
      node.__unit = m[2];
      prev.__conv = convNum(prev.data.trim(), m[2]);
      prev.data = prev.__conv;
      node.data = node.data.replace(SPLIT, `$1${m[1] || m[2] === "″" || m[2] === '"' ? "" : ""}${m[2] === '"' || m[2] === "″" ? " " : ""}${SPLIT_CONV[m[2]][2]}`);
      return;
    }
    const ns = node.nextSibling || (p.childNodes.length === 1 ? p.nextSibling : null);
    const unitNode = ns?.nodeType === 1 ? ns.firstChild : ns;
    if (unitNode?.__unit && NUMERIC.test(node.data.trim())) { node.__conv = convNum(node.data.trim(), unitNode.__unit); node.data = node.__conv; return; }
    const next = metricize(node.data);
    if (next !== node.data) node.data = next;
  };
  const walk = (n) => { if (n.nodeType === 3) fix(n); else n.childNodes.forEach(walk); };
  walk(root);
  new MutationObserver((list) => list.forEach((m) => {
    if (m.type === "characterData") fix(m.target);
    m.addedNodes.forEach(walk);
  })).observe(root, { subtree: true, childList: true, characterData: true });
}
